import { targetConfig } from '@decodo/utils';
import { useMemo, useState } from 'react';

import { UseFormReturn } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { requestLanguages } from '@/blocks/Scraper/constants/languages';
import { responseFormatViews } from '@/blocks/Scraper/constants/response-formats';
import { getStringifiedCopyPayload } from '@/blocks/Scraper/helpers';
import { CodeBlockItem } from '@/components/atoms/CodeBlock/CodeBlockItem';
import { Tabs } from '@/components/atoms/Tabs';
import { Tab } from '@/components/atoms/Tabs/Tab';

import { EmptyRichResponse } from './components/EmptyRichResponse';
import { LangToggle } from './components/LangToggle';
import { buildRequestCode } from './helpers';
import { HTMLResponseView } from './HTMLResponseViewer';
import { JSONResponseView } from './JSONResponseView';
import { useScraper } from './state/context';

type RequestResponseProps = {
  form: UseFormReturn<Record<string, string | boolean>>;
  response: unknown;
  error: unknown;
};

const tabContainerStyle =
  'bg-transparent lg:h-[420px] h-[320px] overflow-y-auto text-body-m';

export const RequestResponse = ({
  form,
  response,
  error,
}: RequestResponseProps) => {
  const { target, activeTab, setActiveTab } = useScraper();

  const { texts } = useTranslation();
  const [requestLanguageIndex, setRequestLanguageIndex] = useState(0);
  const [responseFormatIndex, setResponseFormatIndex] = useState(0);

  const activeRequestLanguage = requestLanguages[requestLanguageIndex];

  const requestCode = buildRequestCode({
    formValues: form.values as Record<string, string | boolean>,
    language: activeRequestLanguage,
    target: target,
  });

  const responseFormat = targetConfig[target.targetKey]?.format || 'json';
  const selectedResponseFormatView = responseFormatViews[responseFormatIndex];

  const languageToggleProps = {
    activeTab,
    copyText: getStringifiedCopyPayload(
      activeTab === 'request' ? requestCode : response
    ),
    hasResponse: !!response,
    requestLanguageIndex,
    responseFormatIndex,
    setRequestLanguageIndex,
    setResponseFormatIndex,
  };

  const tabContent = useMemo<Tab<'request' | 'response'>[]>(() => {
    let responseTabContent = (
      <EmptyRichResponse message={texts.scraper.no_response_message} />
    );
    if (response) {
      responseTabContent = (
        <>
          <LangToggle {...languageToggleProps} />

          <div className={cn(tabContainerStyle)}>
            {responseFormat === 'json' ? (
              <JSONResponseView
                viewType={selectedResponseFormatView}
                data={(response as any)?.results?.[0]?.content}
              />
            ) : (
              <HTMLResponseView
                viewType={selectedResponseFormatView}
                data={response}
              />
            )}
          </div>
        </>
      );
    } else if (error) {
      responseTabContent = (
        <div className={cn(tabContainerStyle, 'py-2')}>
          <JSONResponseView viewType='text' data={error} />
        </div>
      );
    }

    return [
      {
        value: 'request',
        label: texts.scraper.tabs.request,
        content: (
          <>
            <LangToggle {...languageToggleProps} />

            <CodeBlockItem
              classNames={{
                root: 'text-body-m',
              }}
              ContainerProps={{
                className: tabContainerStyle,
              }}
              code={requestCode}
              language={activeRequestLanguage}
            />
          </>
        ),
      },
      {
        value: 'response',
        label: texts.scraper.tabs.response,
        content: responseTabContent,
      },
    ];
  }, [
    requestCode,
    activeRequestLanguage,
    selectedResponseFormatView,
    responseFormat,
    response,
    error,
    texts,
    languageToggleProps,
  ]);

  return (
    <div className='flex flex-col flex-1 h-full rounded dark:border dark:border-gray-300 dark:bg-gray-50 bg-gray-950 border-gray-800 border'>
      <div
        className={cn('relative flex-1 h-full p-3 pt-2')}
        style={{
          // ensures scrollbars match the dark theme on safari
          colorScheme: 'dark',
        }}
      >
        <div className='flex flex-col flex-1 h-full'>
          <div className='flex h-full dark'>
            <Tabs
              value={activeTab}
              classNames={{
                tab: 'text-body-m-medium ',
                tabContent: 'pb-4 pt-0',
                innerContentContainer: '!bg-gray-50',
              }}
              onValueChange={setActiveTab}
              label={texts.scraper.code_block_label}
              tabs={tabContent}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
